import { StructuredDataStatus } from '@repo/shared/dtos/structured-data'
import { Button } from '@repo/ui/button'
import { useT } from '@repo/ui/hooks'
import { cn } from '@repo/ui/utils'
import { AlertCircle, CheckCircle2, Clock, DatabaseBackupIcon, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useSelector } from 'react-redux'

import { useStoreDispatch } from '@/store/hooks'
import { selectStructuredDataState } from '@/store/structured-data-state/selectors'
import { refreshBeneficiariesStructuredData } from '@/store/structured-data-state/thunks'

export function ProcessingStatus() {
  const structuredDataState = useSelector(selectStructuredDataState)
  if (!structuredDataState) return null
  return (
    <Status
      currentStatus={structuredDataState.currentStatus}
      totalBeneficiaries={structuredDataState.totalBeneficiaries}
      totalUpdatedBeneficiaries={structuredDataState.totalUpdatedBeneficiaries}
    />
  )
}

export function Status({
  currentStatus,
  totalBeneficiaries,
  totalUpdatedBeneficiaries,
}: {
  currentStatus: StructuredDataStatus
  totalBeneficiaries: number
  totalUpdatedBeneficiaries: number
}) {
  const { t, tc } = useT({ keyPrefix: 'processingStatus' })

  const buildStatusConfig = (currentStatus: StructuredDataStatus) => {
    switch (currentStatus) {
      case StructuredDataStatus.PROCESSING:
        return {
          icon: Loader2,
          iconClassName: 'text-primary-400 animate-spin',
          label: t(`${currentStatus}`),
          labelClassName: 'text-primary-400',
          showProgress: true,
        }
      case StructuredDataStatus.UPDATED:
        return {
          icon: CheckCircle2,
          iconClassName: 'text-primary-500',
          label: t(`${currentStatus}`),
          labelClassName: 'text-primary-500',
          showProgress: false,
        }
      case StructuredDataStatus.OUTDATED:
        return {
          icon: AlertCircle,
          iconClassName: 'text-amber-500',
          label: t(`${currentStatus}`),
          labelClassName: 'text-amber-500',
          showProgress: false,
        }
      case StructuredDataStatus._DEFAULT:
      default:
        return {
          icon: Clock,
          iconClassName: 'text-gray-400',
          label: t('_default'),
          labelClassName: 'text-gray-400',
          showProgress: false,
        }
    }
  }

  const config = buildStatusConfig(currentStatus)

  const progress =
    totalBeneficiaries > 0 ? (totalUpdatedBeneficiaries / totalBeneficiaries) * 100 : 0

  const Icon = config.icon
  return (
    <div className="flex flex-col gap-1 rounded-lg border-4 border-primary-900 bg-white px-4 py-3 text-primary shadow-xl">
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-primary-600 uppercase">{t('title')}</span>
      </div>
      <div className="flex items-center gap-2">
        <Icon className={cn('h-4 w-4', config.iconClassName)} />
        <span className={cn('text-sm', config.labelClassName)}>{config.label}</span>
      </div>

      {currentStatus === StructuredDataStatus.OUTDATED && (
        <ProcessingButton label={tc('update', { cfl: true })} disabled={false} />
      )}

      {config.showProgress && (
        <>
          <div className="relative h-2 w-full overflow-hidden rounded-full bg-primary-800">
            <div
              className="h-full bg-primary-400 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-primary-300">
            <span>
              {totalUpdatedBeneficiaries} / {totalBeneficiaries}
            </span>
            <span>{Math.round(progress)}%</span>
          </div>
        </>
      )}
    </div>
  )
}

export function ProcessingButton({ disabled, label }: { disabled: boolean; label: string }) {
  const [startProcessing, setStartProcessing] = useState(false) //FIXME JDOUDOU use a enable reprocessing field in store instead
  const dispatch = useStoreDispatch()
  const handleRefresh = () => {
    setStartProcessing(true)
    void dispatch(refreshBeneficiariesStructuredData())
  }
  return (
    <Button disabled={disabled || startProcessing} onClick={handleRefresh} variant="highlight">
      <DatabaseBackupIcon className="size-4" />
      {label}
    </Button>
  )
}
